import { isAdyenRefusalError } from '@/errorCodes/adyen';

import { captureError } from '@/lib/errors';

// Adyen refusal reasons caused by payment infrastructure rather than shopper
// input, card state, issuer decisions, or an explicit cancellation.
const TECHNICAL_REFUSAL_REASON_CODES = new Set([
  '4', // Acquirer Error
  '9', // Issuer Unavailable
  '19', // PIN validation not possible
  '21', // Payment was not submitted
  '39', // 3DS RReq not received from directory server
  '40', // Current AID is in penalty box
  '42', // 3DS authentication error
]);

const PAYMENT_FAILURE_TITLES = {
  adyen_sdk_error: 'Adyen SDK failed',
  invalid_non_adyen_gateway_configuration:
    'Non-Adyen gateway configuration is invalid',
  invalid_post_action_data: 'Payment redirect POST data is incomplete',
  missing_additional_action_data: 'Adyen additional action data is missing',
  missing_order_after_order_creation: 'Order creation returned no order',
  missing_pay_per_link_token_for_remaining_balance:
    'Payment link token is missing for the remaining balance',
  non_adyen_payment_failed: 'Non-Adyen payment submission failed',
  offline_payment_failed: 'Offline payment processing failed',
  offline_payment_toggle_api_errors: 'Offline payment API returned errors',
  offline_payment_toggle_empty_response:
    'Offline payment API returned an empty response',
  order_create_api_errors: 'Order creation API returned errors',
  payment_gateway_initialize_api_errors:
    'Payment gateway initialization returned errors',
  payment_redirect_failed: 'Payment provider redirect failed',
  payment_submission_failed: 'Payment submission failed unexpectedly',
  pay_per_link_creation_returned_no_order:
    'Payment-link creation returned no order',
  pay_per_link_generation_failed: 'Payment-link generation failed',
  transaction_authorization_failed:
    'Payment authorization failed for a technical reason',
  transaction_initialize_api_errors:
    'Transaction initialization returned API errors',
  transaction_process_empty_response:
    'Transaction processing returned an empty response',
  unexpected_transaction_result: 'Adyen returned an unexpected result',
  unsupported_additional_action_method:
    'Adyen returned an unsupported additional-action method',
  unsupported_post_action_payment_method:
    'Adyen returned an unsupported POST-action payment method',
} as const;

type PaymentFailureReason = keyof typeof PAYMENT_FAILURE_TITLES;

type PaymentAnomalyContext = {
  checkoutId?: string | null;
  countryCode?: string | null;
  currency?: string | null;
  errorCodes?: string[];
  failureReason: PaymentFailureReason;
  gatewayId?: string | null;
  isMulticard?: boolean;
  locale?: string | null;
  orderId?: string | null;
  paymentMethod?: string | null;
  refusalReasonCode?: string | null;
  resultCode?: string | null;
  source: string;
  technicalMessage?: string | null;
  transactionId?: string | null;
  transactionType?: string | null;
};

const getPaymentFlow = (source: string) => {
  if (source.startsWith('order.payment')) return 'pay_per_link';
  if (source.includes('review_express')) return 'express_checkout';
  if (source.startsWith('checkout.payment')) return 'standard_checkout';
  return 'shared_payment_handler';
};

const getPaymentStage = (source: string) =>
  source.split('.').at(-1) ?? 'unknown';

const humanizeFailureReason = (failureReason: PaymentFailureReason) =>
  PAYMENT_FAILURE_TITLES[failureReason];

const sanitizeTechnicalMessage = (message?: string | null) => {
  if (!message) return null;

  return message
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[redacted-email]')
    .replace(/\b\d{12,19}\b/g, '[redacted-number]')
    .slice(0, 500);
};

class PaymentWorkflowAnomaly extends Error {
  constructor(failureReason: PaymentFailureReason) {
    super(`Payment anomaly — ${humanizeFailureReason(failureReason)}`);
    this.name = 'PaymentWorkflowAnomaly';
  }
}

/**
 * Customer/card outcomes are useful in checkout UI but are not operational
 * anomalies. Adyen may return the refusal reason as either the result code or
 * the refusal reason code, so check both values.
 */
export const isExpectedPaymentFailure = ({
  refusalReasonCode,
  resultCode,
}: Pick<PaymentAnomalyContext, 'refusalReasonCode' | 'resultCode'>) => {
  if (
    (refusalReasonCode &&
      TECHNICAL_REFUSAL_REASON_CODES.has(refusalReasonCode)) ||
    (resultCode && TECHNICAL_REFUSAL_REASON_CODES.has(resultCode))
  ) {
    return false;
  }

  return Boolean(
    (resultCode &&
      isAdyenRefusalError(resultCode, refusalReasonCode ?? undefined)) ||
    (refusalReasonCode && isAdyenRefusalError('', refusalReasonCode)),
  );
};

/**
 * Emit a searchable exception event with a useful title and stable grouping.
 * Identifiers and technical details stay in non-indexed context, while only
 * low-cardinality diagnostic values are promoted to tags.
 */
export const capturePaymentAnomaly = (context: PaymentAnomalyContext) => {
  if (isExpectedPaymentFailure(context)) return;

  const errorCodes = context.errorCodes?.filter(Boolean).join(',');
  const technicalMessage = sanitizeTechnicalMessage(context.technicalMessage);
  const safeContext = { ...context, technicalMessage };

  captureError(new PaymentWorkflowAnomaly(context.failureReason), {
    fingerprint: [
      'payment-workflow-anomaly',
      context.source,
      context.failureReason,
    ],
    level: 'error',
    tags: {
      ...(context.countryCode && { 'payment.country': context.countryCode }),
      ...(context.currency && { 'payment.currency': context.currency }),
      ...(errorCodes && { 'payment.error_codes': errorCodes }),
      'payment.failure_reason': context.failureReason,
      'payment.flow': getPaymentFlow(context.source),
      ...(context.gatewayId && { 'payment.gateway': context.gatewayId }),
      ...(context.paymentMethod && { 'payment.method': context.paymentMethod }),
      ...(context.isMulticard !== undefined && {
        'payment.multicard': context.isMulticard,
      }),
      ...(context.locale && { 'payment.locale': context.locale }),
      ...(context.refusalReasonCode && {
        'payment.refusal_reason': context.refusalReasonCode,
      }),
      ...(context.resultCode && { 'payment.result': context.resultCode }),
      'payment.source': context.source,
      'payment.stage': getPaymentStage(context.source),
      ...(context.transactionType && {
        'payment.transaction_type': context.transactionType,
      }),
      'payment.workflow': 'checkout',
    },
    extra: {
      paymentAnomaly: {
        ...safeContext,
      },
    },
  });
};
